import React, { useState, useEffect } from 'react';
import { X, Heart, Images, Flame, ChevronRight, Sparkles } from 'lucide-react';
import { PRE_REGISTRO_2027_URL } from '../config/constants';
import { trackPreregistroClick } from '../hooks/usePreregistroCount';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

export const AFTERMOVIE_ID = 'uIvWAcjdIpc';

const ThankYouPopup = ({ delay = 2500 }) => {
  const { language } = useLanguage();
  const isES = language === 'es';
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('thankYouPopupSeen')) return;
    const timer = setTimeout(() => {
      const kick = () => setOpen(true);
      if ('requestIdleCallback' in window) window.requestIdleCallback(kick, { timeout: 3000 });
      else setTimeout(kick, 0);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem('thankYouPopupSeen', 'true');
  };

  const goTo = (id) => {
    close();
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="thank-you-title" data-testid="thank-you-popup">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close}></div>
      <div className="relative w-full max-w-2xl bg-[#071528] border border-[#D4AF37]/30 rounded-3xl overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.7)]">
        <div className="relative z-10 bg-gradient-to-r from-[#D4AF37] via-[#F3D27A] to-[#D4AF37] text-[#071528] text-center py-2.5 px-4 text-[11px] sm:text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2" data-testid="thank-you-band">
          <Sparkles className="w-3.5 h-3.5" />
          {isES ? '20ª Convención Nacional · Un éxito histórico' : '20th National Convention · A historic success'}
          <Sparkles className="w-3.5 h-3.5" />
        </div>
        <button onClick={close} className="absolute top-12 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" aria-label={isES ? 'Cerrar' : 'Close'} data-testid="thank-you-close">
          <X className="w-5 h-5" />
        </button>

        <div className="relative aspect-video bg-black">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${AFTERMOVIE_ID}?rel=0&modestbranding=1&playsinline=1`}
            title={isES ? 'Aftermovie oficial · 20ª Convención Nacional ASOFOM' : 'Official aftermovie · 20th ASOFOM National Convention'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid="thank-you-aftermovie"
          />
        </div>

        <div className="relative px-6 sm:px-8 pt-6 pb-6 text-center">
          <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-72 h-40 bg-[#FF6B35]/20 rounded-full blur-[80px] pointer-events-none" aria-hidden="true"></div>
          <div className="relative inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-full px-3.5 py-1.5 mb-3">
            <Heart className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs font-bold text-[#D4AF37] uppercase tracking-wide">{isES ? 'Aftermovie oficial' : 'Official aftermovie'}</span>
          </div>
          <h2 id="thank-you-title" className="relative text-2xl sm:text-3xl font-bold text-white leading-tight mb-2">
            {isES ? '¡GRACIAS por hacer historia con nosotros!' : 'THANK YOU for making history with us!'}
          </h2>
          <p className="relative text-sm sm:text-base text-white/65 leading-relaxed mb-5 max-w-xl mx-auto">
            {isES
              ? 'Tres días en Cancún que marcaron 20 años de legado. Revive los mejores momentos y pre-regístrate: sé de los primeros en conocer todos los detalles de la 21ª Convención.'
              : 'Three days in Cancún that marked 20 years of legacy. Relive the best moments and pre-register: be among the first to know every detail of the 21st Convention.'}
          </p>
          <div className="relative flex flex-col sm:flex-row gap-3">
            <Button onClick={() => goTo('gracias')} className="flex-1 bg-[#FF6B35] hover:bg-[#E55A28] text-white font-bold py-6 text-base shadow-lg" data-testid="thank-you-gallery-cta">
              <Images className="w-5 h-5 mr-2" />
              {isES ? 'Ver galería y compartir' : 'View gallery & share'}
            </Button>
            <Button asChild variant="outline" className="flex-1 border-2 border-[#D4AF37] bg-[#D4AF37]/15 text-white hover:bg-[#D4AF37] hover:text-[#071528] font-bold py-6 text-base" data-testid="thank-you-preregistro-cta">
              <a href={PRE_REGISTRO_2027_URL} target="_blank" rel="noopener noreferrer" onClick={trackPreregistroClick}>
                <Flame className="w-5 h-5 mr-2" />
                {isES ? 'Pre-regístrate para 2027' : 'Pre-register for 2027'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </Button>
          </div>
          <button onClick={close} className="w-full text-center text-white/45 hover:text-white/70 text-sm font-medium mt-4 transition-colors" data-testid="thank-you-dismiss">
            {isES ? 'Cerrar' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPopup;
